<script setup lang="ts">
import { onMounted, ref, shallowRef } from 'vue'
import SessionFlow from '@/components/voice-session/SessionFlow.vue'
import type { SessionExitDetails } from '@/components/voice-session/flow'
import type { GameSessionInfo } from '@/composables/voice-session/sessionApi'
import type { GameExitReason } from '@/types/gameExit'
import { $api } from '@/utils/api'
import { useUserStore } from '@core/stores/user'

// Новый экран сессии игры против ИИ — отдельная страница без меню приложения. Сюда ведёт
// `games/play/[id]`, когда у организации включена новая версия; ссылки в списках игр не меняются.
// Версию решает `new_session_screen` из `game-session` — текущий флаг организации, а не снимок
// флагов при входе. Без флага адрес ведёт на прежний экран той же игры.
definePage({
  meta: {
    layout: 'blank',
    action: 'read',
    subject: 'games',
  },
})

const route = useRoute('games-session-id')
const router = useRouter()
const { t } = useI18n()
const userStore = useUserStore()

const gameId = String(route.params.id)
const screen = ref<'loading' | 'games-blocked' | 'flow'>('loading')
const gameSession = shallowRef<GameSessionInfo | null>(null)

onMounted(async () => {
  const [game, session, maintenance] = await Promise.all([
    $api<{ status?: string }>(`/games/${gameId}`).catch(() => null),
    $api<GameSessionInfo>(`/voice-agent/game-session/${gameId}`).catch(() => null),
    $api<{ gamesBlocked?: boolean }>('/maintenance/status').catch(() => null),
  ])

  if (game?.status === 'completed') {
    await router.replace({ name: 'games-detail-id', params: { id: gameId } })

    return
  }

  // Флаг выключен или ответа нет — прежний экран: он же разберёт и ошибку доступа.
  if (session?.new_session_screen !== true) {
    await router.replace({ name: 'games-play-id', params: { id: gameId } })

    return
  }

  if (maintenance?.gamesBlocked) {
    screen.value = 'games-blocked'

    return
  }

  gameSession.value = session
  screen.value = 'flow'
})

/**
 * Выход — как у прежнего экрана: разговор не состоялся — к списку игр, иначе — к игре
 * (`just_finished` — игра уже была закрыта, `end_not_sent` — завершение не дошло до сервера).
 * `replace`: к экрану сессии кнопкой «Назад» возвращаться незачем.
 */
async function handleExit(reason: GameExitReason, details: SessionExitDetails): Promise<void> {
  if (reason === 'aborted') {
    await router.replace({ name: 'games-list' })

    return
  }

  let query: Record<string, string> | undefined

  if (reason === 'already-finished')
    query = { just_finished: '1' }
  else if (details.endNotSent)
    query = { end_not_sent: '1' }

  await router.replace({ name: 'games-detail-id', params: { id: gameId }, query })
}

function goToSessions(): void {
  router.push({ name: 'games-list' })
}
</script>

<template>
  <div
    v-if="screen === 'loading'"
    class="d-flex align-center justify-center"
    style="min-block-size: 100vh;"
  >
    <VProgressCircular
      indeterminate
      size="48"
      color="primary"
    />
  </div>

  <VContainer
    v-else-if="screen === 'games-blocked'"
    class="d-flex align-center justify-center"
    style="min-block-size: 80vh;"
  >
    <VAlert
      type="warning"
      prominent
      max-width="600"
      class="text-center"
    >
      {{ t('maintenance-page.games_blocked_message') }}
    </VAlert>
  </VContainer>

  <SessionFlow
    v-else
    entry="app"
    :game-id="gameId"
    :game-session="gameSession"
    :exit-handler="handleExit"
    :participant-name="userStore.fullName || null"
    @back="goToSessions"
  />
</template>
